import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { get, uniqueId } from 'lodash';
import axios from 'axios';
import {
	SdfAlert,
	SdfBusyIndicator,
	SdfButton,
	SdfIcon,
	SdfWizardLayout,
	SdfWizardStepsVertical,
	SdfWizardStepsVerticalItem
} from '@synerg/react-components';
import { PartnerTypeAndPlatform } from '../../common/partnerTypeAndPlatform';
import StepOne from './steps/stepOne';
import StepTwo from './steps/stepTwo'; // Marketplace integration step
import StepThree from './stepsDC/stepTwo'; // Use DC's use case selection step
import StepFour from './stepsDC/stepThree'; // Review step (rename as needed)

export default function ProjectStepsB2C({ hideModal, isB2CProject = false }) {
	const [useCases, setUseCases] = useState([]);
	const [, setProducts] = useState([]);

	const [loading, setLoading] = useState(false);

	const [applicationNameList, setApplicationNameList] = useState(undefined);
	const [error, setError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	// B2C projects use WFN (mid-sized) defaults
	const defaultValues = {
		projectName: '',
		projectDescription: '',
		companySize: 'mid',
		useCaseID: null,
		useCaseName: null,
		appStore: 'US',
		platform: PartnerTypeAndPlatform.marketplace.toLowerCase(),
		productIntegration: 'ADP Workforce Now®'
	};

	const { control, formState, getValues, setValue } = useForm({ mode: 'onChange', defaultValues });

	const [activeStep, setActiveStep] = useState(0);
	const [validForm, setValidForm] = useState(true);

	const [selectedRadioBtnIndex, setSelectedRadioBtnIndex] = useState(0);
	const [selectedUseCaseBtnIndex, setSelectedUseCaseBtnIndex] = useState(null);

    useEffect(() => {
			async function getUseCases() {
				setLoading(true);
				try {
					Promise.all([
						axios(`${import.meta.env.VITE_ROOT_PATH}/api/organization/applications/name`),
						axios(`${import.meta.env.VITE_ROOT_PATH}/api/project/use-cases`),
						axios(`${import.meta.env.VITE_ROOT_PATH}/api/products`,
								{params: {includeDataCloud: false}})]).then(([
						{data: appNameList, status: appNameStatus}, {
							data: useCases,
							status: useCasesStatus,
						}, {data: products, status: productsStatus}]) => {
						appNameStatus === 200 ? setApplicationNameList(appNameList) : setErrorMessage(
								'Something went wrong when trying to get application name list. Please try again.');
						useCasesStatus === 200 ? setUseCases(useCases) : setErrorMessage(
								'Something went wrong when trying to get use-cases. Please try again.');
						productsStatus === 200 ? setProducts(products) : setErrorMessage(
								'Something went wrong when trying to get product list. Please try again.');
						if (appNameStatus !== 200 || useCasesStatus !== 200 || productsStatus !== 200) {
							setError(true);
						}
						setLoading(false);
					}).catch((e) => {
						setLoading(false);
						setErrorMessage(e.message);
						setError(true);
					});
				} catch (e) {
					setLoading(false);
					setError(true);
				}
			}

			getUseCases();
			// eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

	useEffect(() => {
		if (formState.isValid) {
			setValidForm(true);
		} else {
			setValidForm(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formState.isValid]);

	useEffect(() => {
	  // On mount, if selectedRadioBtnIndex is 0, set appStore to 'US'
	  if (activeStep === 1 && selectedRadioBtnIndex === 0 && !getValues().appStore) {
	    setValue('appStore', 'US');
	  }
	}, [activeStep]);

	const backClickHandler = () => {
		if (activeStep !== 0) {
			setActiveStep(activeStep - 1);
		}
	};

	const nextClickHandler = async() => {
		if (activeStep < 3) {
			setActiveStep(activeStep + 1);
		} else {
			await createProject();
		}
	};

	const createProject = async () => {
		setLoading(true);
		try {
			const dataToSend = {
				appStore: getValues().appStore,
				companySize: getValues().companySize,
				platform: getValues().platform,
				projectDescription: getValues().projectDescription,
				projectName: getValues().projectName,
				useCaseID: getValues().useCaseID
			}

			const { data } = await axios.post(`${import.meta.env.VITE_ROOT_PATH}/api/project`, dataToSend);
			if (data._id) {
				hideModal(false);
			} else {
				setError(true);
				setErrorMessage('Unable to create project. Please try again');
			}
		} catch (e) {
			const errorMessage = get(e, 'response.data.message', 'Something went wrong when trying to create project. Please try again.');
			setErrorMessage(errorMessage);
			setError(true);
		}
		setLoading(false);
	}

	function canGoNext() {
		switch (activeStep) {
			case 0:
				return validForm;
			case 1:
				return !!getValues().appStore;
			case 2:
				return selectedUseCaseBtnIndex !== null;
			case 3:
				return true;
			default:
				return false;
		}
	}

	const ShowError = ({message}) => {
		return (
			<SdfAlert key={uniqueId()} status={'error'} showToast={true}>
				<span slot="title"> Error </span>
				<div>{message || 'Something went wrong, please try again later'}</div>
			</SdfAlert>
		);
	};

	const dispatch = ({ type, payload }) => {
	  if (type === 'UPDATE') {
	    Object.entries(payload).forEach(([key, value]) => setValue(key, value));
	  }
	};

	return (<div>
		{loading && <SdfBusyIndicator style={{color: '#1a8099'}} size="xl" fullScreen={true} />}
		<SdfWizardLayout>
			{error && <div slot="page-content" className="px-8 mb-2 min-w-fit">
				<ShowError message={errorMessage}/>
			</div>}
			<div slot="page-content" className="px-8 hxl overflow-y-scroll">
    {activeStep === 0 && <StepOne state={getValues()} control={control} setError={setError}
      applicationNameList={applicationNameList} loadingAppNames={loading} />}
    {activeStep === 1 && <StepTwo state={getValues()} dispatch={dispatch} selectedRadioBtnIndex={selectedRadioBtnIndex} setSelectedRadioBtnIndex={setSelectedRadioBtnIndex} />}
    {activeStep === 2 && (
			<StepThree
				state={getValues()}
				useCases={useCases.filter(u => u.locale === getValues().appStore)}
				selectedUseCaseBtnIndex={selectedUseCaseBtnIndex}
				setSelectedUseCaseBtnIndex={setSelectedUseCaseBtnIndex}
				setValue={setValue}
			/>
		)}
    {activeStep === 3 && <StepFour state={getValues()} isB2CProject={true} />}
</div>

			<SdfWizardStepsVertical slot="wizard-steps" className="flex-grow-0 w-64" style={{marginTop: '30px'}}>
    <SdfWizardStepsVerticalItem className="w-48" stepStatus="visited" isCurrent={activeStep === 0}>
        Project details
    </SdfWizardStepsVerticalItem>
    <SdfWizardStepsVerticalItem className="w-64" isCurrent={activeStep === 1} stepStatus={activeStep > 1 ? 'visited' : 'unvisited'}>
        Marketplace integration
    </SdfWizardStepsVerticalItem>
    <SdfWizardStepsVerticalItem className="w-64" isCurrent={activeStep === 2} stepStatus={activeStep > 2 ? 'visited' : 'unvisited'}>
        Use case selection
    </SdfWizardStepsVerticalItem>
    <SdfWizardStepsVerticalItem className="w-64" stepStatus={activeStep > 3 ? 'visited' : 'unvisited'} isCurrent={activeStep === 3}>
        Review
    </SdfWizardStepsVerticalItem>
</SdfWizardStepsVertical>

			<SdfButton slot="buttons" emphasis="tertiary" variant="contained" onClick={() => {hideModal(false);}}> Cancel </SdfButton>
			{activeStep > 0 && <SdfButton slot="buttons" variant="contained" onClick={backClickHandler}>
				<SdfIcon icon="nav-move-left" /> Back
			</SdfButton>}
			<SdfButton disabled={!canGoNext() ? 'true' : undefined} slot="buttons" emphasis="primary" iconPlacement="after"
					variant="contained" onClick={nextClickHandler}>
				{activeStep === 3 ? 'Generate' : 'Next'}
				{activeStep !== 3 && <SdfIcon icon="nav-move-right" />}
			</SdfButton>

		</SdfWizardLayout>
	</div>);
}
